import { PROGRAM_KINDS, type GrantSupportFilter, type ProgramKind } from '@zoxo/shared';

/**
 * 주소 → 기둥 (docs/REFACTOR.md §4, 2026-09-24 형이 정한 트리).
 *
 *   /news                 전체
 *   /news/grants          자금·바우처
 *   /news/loans           융자·보증
 *   /news/facilities      시설·입주·공간
 *   /news/programs        프로그램
 *   /news/programs/<slug> 프로그램 하위 종류 (learning · networking · contests · fairs · sales · overseas · other)
 *
 * 옛 주소(`/grants` `/loans` `/facilities` `/programs?kind=` `/subsidies` `/vouchers`)는 server.mjs 가 301 로 보낸다.
 *
 * **SSR 과 SPA 가 같은 표를 본다.** 한쪽에만 주소를 더하면 그 주소가 클라이언트
 * 에서만 살아 검색엔진에는 빈 껍데기로 보인다. 라우터 훅을 안 쓰는 순수 표라 `entry-server` 도 그대로 읽는다.
 */
export const TYPE_ROUTES: Record<string, GrantSupportFilter> = {
  '/news/grants': 'grant',
  '/news/loans': 'loan',
  '/news/facilities': 'facility',
  '/news/programs': 'program',
};

/** 기둥 → 주소. 카드·패널이 링크를 만들 때 쓴다. */
export const TYPE_PATH: Record<GrantSupportFilter, string> = {
  grant: '/news/grants',
  loan: '/news/loans',
  facility: '/news/facilities',
  program: '/news/programs',
};

/** 프로그램 하위 종류 → 주소 조각. 형이 정한 영문 복수형이라 키와 다르다 (contest → contests). */
export const PROGRAM_KIND_SLUG: Record<ProgramKind, string> = {
  learning: 'learning',
  networking: 'networking',
  contest: 'contests',
  fair: 'fairs',
  sales: 'sales',
  overseas: 'overseas',
  other: 'other',
};

export const programKindPath = (kind: ProgramKind): string => `/news/programs/${PROGRAM_KIND_SLUG[kind]}`;

/** 주소 조각 → 하위 종류. 모르는 조각이면 null (404). */
export function programKindFromSlug(slug: string | undefined | null): ProgramKind | null {
  if (!slug) return null;
  const hit = PROGRAM_KINDS.find((k) => PROGRAM_KIND_SLUG[k] === slug);
  return hit ?? null;
}

/** `/news/programs/<slug>` 이면 그 종류, 아니면 null. SSR 이 경로를 가를 때 쓴다. */
export function programKindFromPath(path: string): ProgramKind | null {
  const m = /^\/news\/programs\/([a-z]+)\/?$/.exec(path);
  return m ? programKindFromSlug(m[1]) : null;
}

/**
 * 기둥별 제목과 한 줄 설명.
 *
 * **SSR 과 SPA 가 같은 문구를 쓴다.** 한쪽에만 두면 첫 페인트에서 제목이
 * 바뀐다 — 실제로 그렇게 나갔다가 잡았다: SSR 이 「정부지원사업」을 그리고
 * SPA 가 「보조금」으로 덮었다.
 */
export const TYPE_BLURB: Record<GrantSupportFilter, string> = {
  grant: '갚지 않는 돈. 보조금 · 바우처 · 상금. 기업당 한도와 자부담 비율을 공고문에서 뽑는다.',
  loan: '갚는 돈과 보증. 한도 · 금리 · 기간, 이차보전 폭까지 본다.',
  facility: '입주 · 사무공간 · 공장 · 장비 공동활용. 어디서, 얼마나 오래.',
  program: '그 밖의 모든 것. 교육·컨설팅·멘토링 · 네트워킹·행사 · 공모전 · 전시·박람회 · 판로 · 수출.',
};
